import { canExploreSignupDashboard } from '@/components/signup/activation';
import { AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  Loader2,
  MailOpen,
  MapPin,
  MessageSquareDot,
  Phone,
  Pin,
  Sparkles,
  SquarePen,
  TriangleAlert,
  User,
  Users,
} from 'lucide-react';
import moment from 'moment';
import { Component } from 'react';
import { connect } from 'react-redux';
import { displayNotification } from '@/actions/settings';
import { setSelectedConversations } from '@/actions/userInterface';
import {
  setCustomerConversationChannelFilter,
  setOpenOrClosedConversationsSelected,
  toggleConversationOpen,
  toggleConversationPin,
  toggleMarkAsReadConversation,
} from '@/actions/webchat';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Orb } from '@/components/ai-employee/setup/parts/orb';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { HideMobileTabBar } from '@/components/mobile';
import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ContextMenu, ContextMenuTrigger } from '@/components/ui/context-menu';
import { IconButton } from '@/components/ui/custom/IconButton';
import { SoundWave } from '@/components/ui/custom/SoundWave';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  getConversationCustomers,
  getConversationDisplayName,
  getConversationListName,
} from '@/types';
import { findActiveCustomerCall } from '@/utils/activeCustomerCall';
import { formatTypingNames } from '@/utils/formatTypingNames';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import { maskPhone } from '@/utils/maskPhone';
import InboxEmpty from '../../utils/icons/InboxEmpty';
import { ActionBar } from './ActionBar';
import { aiDraftChip } from './aiDraft';
import ChannelFilter from './ChannelFilter';
import ConversationPinMenuItems from './ConversationPinMenuItems';
import ConversationSkeleton from './ConversationSkeleton';
import CreateFolderDialog from './CreateFolderDialog';
import CustomerConversationContextMenuContent from './CustomerConversationContextMenuContent';
import CustomerSearchBar from './CustomerSearchBar';
import GroupAvatar from './GroupAvatar';
import { ConversationActionSheet } from './inbox-mobile/ConversationActionSheet';
import {
  InboxMobileFilters,
  InboxMobileHeader,
  InboxViewsSheet,
  MobileRowSkeletons,
  getInboxViewTitle,
} from './inbox-mobile/InboxMobileHeader';
import { MobileBulkBar } from './inbox-mobile/MobileBulkBar';
import { MobileConversationRow } from './inbox-mobile/MobileConversationRow';
import { isConversationReadForUser } from './inbox-mobile/useConversationQuickActions';
import { VoiceDialerDialog } from './VoiceDialerDialog';

const MAX_TAG_CHAR_LENGTH = 100;

// Toolbar width (px) needed to show every control with its text label. The
// Call button only renders for voice accounts, so the full row needs more
// room there. As the toolbar narrows, the Open/Closed tabs go icon-only first;
// once that saving is used up, Call/New drop their labels too.
const TOOLBAR_FULL_WIDTH = 340;
const TOOLBAR_FULL_WIDTH_WITH_CALL = 420;
const TOOLBAR_ICON_TABS_SAVINGS = 60;
const TOOLBAR_ICON_TABS_SAVINGS_WITH_CALL = 90;

// Maps channel filter values to display names
const CHANNEL_DISPLAY_NAMES = {
  sms: 'SMS',
  email: 'Email',
  googleBusinessMessage: 'Google Business',
  facebook: 'Facebook',
  instagram: 'Instagram',
  webchat: 'Textchat',
};

class CustomerList extends Component {
  constructor(props) {
    super(props);
    this.down = this.down.bind(this); // Bind down function to the instance
    this.toolbarRef = null;
    this.toolbarResizeObserver = null;
    this.state = {
      customerLookupInitialValue: '',
      showNewLayout: false,
      channelFilter: 'All', // Local channel filter state
      voiceDialerDialogIsOpen: false,
      showCreateFolderDialog: false,
      showVoiceUpsell: false,
      toolbarMode: 'full', // 'full' | 'iconTabs' | 'iconsOnly'
      // Phone only: Select mode for bulk actions, the row whose action sheet
      // is open, and the inbox views sheet.
      selectMode: false,
      actionSheetConversationId: null,
      showViewsSheet: false,
      // Phone: the large title has scrolled under the bar, which now shows it.
      titleCollapsed: false,
    };
    this.listScrollRef = null;
    this.largeTitleRef = null;
    this.savedListScrollTop = null;
    this.customerSearchText = '';
    this.loadMoreSentinel = null;
    this.loadMoreObserver = null;
    this.autoLoad = { pending: false, countBefore: 0, exhausted: false };
  }

  hasVoiceProduct = () => Boolean(this.props.auth.products?.includes('voice'));

  measureToolbar = () => {
    if (!this.toolbarRef) return;
    const fullWidth = this.hasVoiceProduct() ? TOOLBAR_FULL_WIDTH_WITH_CALL : TOOLBAR_FULL_WIDTH;
    const width = this.toolbarRef.clientWidth;
    const iconTabsSavings = this.hasVoiceProduct()
      ? TOOLBAR_ICON_TABS_SAVINGS_WITH_CALL
      : TOOLBAR_ICON_TABS_SAVINGS;

    let toolbarMode = 'full';
    if (width < fullWidth - iconTabsSavings) {
      toolbarMode = 'iconsOnly';
    } else if (width < fullWidth) {
      toolbarMode = 'iconTabs';
    }

    if (toolbarMode !== this.state.toolbarMode) {
      this.setState({ toolbarMode });
    }
  };

  setToolbarRef = (node) => {
    if (this.toolbarResizeObserver && this.toolbarRef) {
      this.toolbarResizeObserver.unobserve(this.toolbarRef);
    }
    this.toolbarRef = node;
    if (node && this.toolbarResizeObserver) {
      this.toolbarResizeObserver.observe(node);
    }
    this.measureToolbar();
  };

  /**
   * Generates a human-readable description of the current filter state
   * @returns {string} Description of active filters
   */
  getFilterDescription = () => {
    const { openConversationsSelected, showOnlyUnreadConversations, assignedTypeSelected } =
      this.props;
    const { channelFilter } = this.state;

    const parts = [];

    // Open/Closed status
    parts.push(openConversationsSelected ? 'open' : 'closed');

    // Unread filter
    if (showOnlyUnreadConversations) {
      parts.push('unread');
    }

    // Channel filter
    if (channelFilter && channelFilter !== 'All') {
      const channelName = CHANNEL_DISPLAY_NAMES[channelFilter] || channelFilter;
      parts.push(channelName);
    }

    // Build the conversation type description
    let typeDescription = 'conversations';

    if (assignedTypeSelected) {
      if (assignedTypeSelected === 'ALL') {
        typeDescription = 'conversations in Inbox';
      } else if (assignedTypeSelected === 'UNASSIGNED') {
        typeDescription = 'unassigned conversations';
      } else if (assignedTypeSelected === 'MY_MESSAGES') {
        typeDescription = 'conversations assigned to you';
      } else if (assignedTypeSelected.startsWith('USER_')) {
        const _userId = assignedTypeSelected.split('_')[1];
        const user = this.props.users?.find((u) => u._id === _userId);
        const userName = user?.name || 'this user';
        typeDescription = `conversations assigned to ${userName}`;
      } else if (assignedTypeSelected.startsWith('TAG_')) {
        const [, ...rest] = assignedTypeSelected.split('_');
        const tag = rest.join('_');
        typeDescription = `conversations tagged "${tag}"`;
      } else if (assignedTypeSelected.startsWith('DEPARTMENT_')) {
        const _deptId = assignedTypeSelected.split('_')[1];
        const dept = this.props.departments?.find((d) => d._id === _deptId);
        const deptName = dept?.name || 'this department';
        typeDescription = `conversations in ${deptName}`;
      } else if (assignedTypeSelected.startsWith('LOCATION_')) {
        const _locId = assignedTypeSelected.split('_')[1];
        const location = this.props.businessGroup?.find((b) => b._id === _locId);
        const locName = location?.name || 'this location';
        typeDescription = `conversations at ${locName}`;
      } else if (assignedTypeSelected.startsWith('FOLDER_')) {
        const _folderId = assignedTypeSelected.split('_')[1];
        const folder = this.props.folders?.find((f) => f._id === _folderId);
        const folderName = folder?.name || 'this folder';
        typeDescription = `conversations in "${folderName}"`;
      }
    }

    // Combine: "No open unread SMS conversations in Inbox"
    const prefix = parts.join(' ');
    return `${prefix} ${typeDescription}`;
  };

  /**
   * Gets a short label for the current filter for the "Load more" button
   * @returns {string} Short filter label
   */
  getFilterShortLabel = () => {
    const { assignedTypeSelected, openConversationsSelected } = this.props;
    const { channelFilter } = this.state;

    const parts = [];

    // Status
    parts.push(openConversationsSelected ? 'open' : 'closed');

    // Channel
    if (channelFilter && channelFilter !== 'All') {
      const channelName = CHANNEL_DISPLAY_NAMES[channelFilter] || channelFilter;
      parts.push(channelName);
    }

    // Type
    if (assignedTypeSelected) {
      if (assignedTypeSelected === 'UNASSIGNED') {
        parts.push('unassigned');
      } else if (assignedTypeSelected === 'MY_MESSAGES') {
        parts.push('my');
      } else if (assignedTypeSelected.startsWith('USER_')) {
        const _userId = assignedTypeSelected.split('_')[1];
        const user = this.props.users?.find((u) => u._id === _userId);
        parts.push(user?.name || 'user');
      } else if (assignedTypeSelected.startsWith('TAG_')) {
        const [, ...rest] = assignedTypeSelected.split('_');
        parts.push(`"${rest.join('_')}"`);
      } else if (assignedTypeSelected.startsWith('FOLDER_')) {
        const _folderId = assignedTypeSelected.split('_')[1];
        const folder = this.props.folders?.find((f) => f._id === _folderId);
        parts.push(folder?.name || 'folder');
      }
    }

    return parts.join(' ');
  };

  componentDidMount() {
    document.title = 'Reviews & Messaging';
    this.setState({
      // No auth here: this list never followed the sign-up preview's layout.
      showNewLayout: getShowNewLayout(),
    });
    if (this.props.active === 'webchat') {
      document.addEventListener('keydown', this.down);
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.toolbarResizeObserver = new ResizeObserver(this.measureToolbar);
      if (this.toolbarRef) {
        this.toolbarResizeObserver.observe(this.toolbarRef);
      }
    } else {
      window.addEventListener('resize', this.measureToolbar);
    }
    this.measureToolbar();
  }

  // Crossing the phone breakpoint swaps the scroll container (the whole phone
  // screen scrolls; the desktop list scrolls under its toolbar). Note the row
  // at the top so the other layout can start there.
  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.isMobile === this.props.isMobile || !this.listScrollRef) return null;
    return { anchor: this.getTopVisibleRow(this.listScrollRef) };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const crossedBreakpoint = prevProps.isMobile !== this.props.isMobile;
    if (crossedBreakpoint) {
      this.savedListScrollTop = null;
      requestAnimationFrame(() => this.restoreListPosition(snapshot?.anchor));
    }

    // Phone: once an automatic page comes back, stop when it added nothing for
    // this filter (the API has no end marker), otherwise check the sentinel again.
    if (
      this.autoLoad.pending &&
      prevProps.loadingMoreConversations &&
      !this.props.loadingMoreConversations
    ) {
      this.autoLoad.pending = false;
      if (this.props.customerConversations.length <= this.autoLoad.countBefore) {
        this.autoLoad.exhausted = true;
      } else if (this.loadMoreObserver && this.loadMoreSentinel) {
        this.loadMoreObserver.unobserve(this.loadMoreSentinel);
        this.loadMoreObserver.observe(this.loadMoreSentinel);
      }
    }

    if (this.getFilterKey(prevProps, prevState) !== this.getFilterKey(this.props, this.state)) {
      this.autoLoad.exhausted = false;
    }

    // Phone: the list sits under the open thread. Hiding the tab bar makes it
    // taller, which can clamp its scroll, so put back where the user was.
    if (
      !crossedBreakpoint &&
      this.props.isCoveredOnMobile &&
      !prevProps.isCoveredOnMobile &&
      this.listScrollRef
    ) {
      this.savedListScrollTop ??= this.listScrollRef.scrollTop;
    }
    if (!crossedBreakpoint && !this.props.isCoveredOnMobile && prevProps.isCoveredOnMobile) {
      const scrollTop = this.savedListScrollTop;
      this.savedListScrollTop = null;
      // Check the end of the list again now that it shows.
      if (this.loadMoreObserver && this.loadMoreSentinel) {
        this.loadMoreObserver.unobserve(this.loadMoreSentinel);
        this.loadMoreObserver.observe(this.loadMoreSentinel);
      }
      if (scrollTop != null) {
        requestAnimationFrame(() => {
          if (this.listScrollRef) this.listScrollRef.scrollTop = scrollTop;
        });
      }
    }

    // A different inbox view starts at the top on a phone, like a new screen.
    if (
      this.props.isMobile &&
      prevProps.assignedTypeSelected !== this.props.assignedTypeSelected &&
      this.listScrollRef
    ) {
      this.listScrollRef.scrollTop = 0;
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.down);
    if (this.toolbarResizeObserver) {
      this.toolbarResizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.measureToolbar);
    this.loadMoreObserver?.disconnect();
  }

  getFilterKey = (props, state) =>
    [
      props.assignedTypeSelected,
      props.openConversationsSelected,
      props.showOnlyUnreadConversations,
      state.channelFilter,
    ].join('|');

  setCustomerSearchText = (text) => {
    this.customerSearchText = text;
  };

  // The phone bar sticks over the top of the scrolling screen.
  getStickyHeaderHeight = (scroller) => scroller.querySelector(':scope > header')?.offsetHeight || 0;

  getTopVisibleRow = (scroller) => {
    if (scroller.scrollTop < 1) return null;
    const top = scroller.getBoundingClientRect().top + this.getStickyHeaderHeight(scroller);
    const rows = scroller.querySelectorAll('[data-testid="customer-list-item"]');
    for (let index = 0; index < rows.length; index++) {
      const rect = rows[index].getBoundingClientRect();
      if (rect.bottom > top + 1) return { index, offset: rect.top - top };
    }
    return null;
  };

  // After crossing the phone breakpoint: start the new layout at the row that
  // was at the top, and keep the open conversation's row in view once the
  // list is back beside the thread. Both layouts render rows in the same order.
  restoreListPosition = (anchor) => {
    const scroller = this.listScrollRef;
    if (!scroller) return;
    const rows = scroller.querySelectorAll('[data-testid="customer-list-item"]');

    const anchorRow = anchor && rows[anchor.index];
    if (anchorRow) {
      const top = scroller.getBoundingClientRect().top + this.getStickyHeaderHeight(scroller);
      scroller.scrollTop += anchorRow.getBoundingClientRect().top - top - anchor.offset;
    }

    const activeId = this.props.activeConversation?._id;
    if (!this.props.isMobile && activeId) {
      const index = this.getVisibleConversations().findIndex((c) => c._id === activeId);
      const activeRow = index >= 0 ? rows[index]?.closest('[role="button"]') || rows[index] : null;
      if (activeRow) {
        const box = scroller.getBoundingClientRect();
        const rect = activeRow.getBoundingClientRect();
        if (rect.top < box.top) scroller.scrollTop -= box.top - rect.top;
        else if (rect.bottom > box.bottom) scroller.scrollTop += rect.bottom - box.bottom;
      }
    }

    // A thread covers the phone list: come back to this spot.
    if (this.props.isMobile && this.props.isCoveredOnMobile) {
      this.savedListScrollTop = scroller.scrollTop;
    }
    this.handleMobileListScroll();
  };

  // Phone: show the title in the bar once the large title scrolls under it.
  handleMobileListScroll = () => {
    if (!this.props.isMobile || !this.listScrollRef) return;
    const threshold = (this.largeTitleRef?.offsetHeight || 44) - 8;
    const titleCollapsed = this.listScrollRef.scrollTop > threshold;
    if (titleCollapsed !== this.state.titleCollapsed) this.setState({ titleCollapsed });
  };

  setLargeTitleRef = (node) => {
    this.largeTitleRef = node;
  };

  setListScrollRef = (node) => {
    this.listScrollRef = node;
    // The sentinel's ref is set first; observe it again with the list as root.
    if (node && this.loadMoreSentinel && this.loadMoreObserver?.root !== node) {
      this.setLoadMoreSentinel(this.loadMoreSentinel);
    }
  };

  // Phone: load the next page before the end of the list comes into view.
  setLoadMoreSentinel = (node) => {
    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect();
      this.loadMoreObserver = null;
    }
    this.loadMoreSentinel = node;
    if (!node || !this.props.isMobile || typeof IntersectionObserver === 'undefined') return;
    this.loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.autoLoadMore();
      },
      { root: this.listScrollRef, rootMargin: '0px 0px 600px 0px' },
    );
    this.loadMoreObserver.observe(node);
  };

  autoLoadMore = () => {
    const { isMobile, isCoveredOnMobile, loading, loadingMoreConversations, customerConversations } =
      this.props;
    // Nothing to load while a thread covers the list.
    if (!isMobile || isCoveredOnMobile || loading || loadingMoreConversations) return;
    if (this.autoLoad.pending || this.autoLoad.exhausted) return;
    this.autoLoad = { pending: true, countBefore: customerConversations.length, exhausted: false };
    this.props.getNextCustomers(this.state.channelFilter);
  };

  isConversationSelected = (conversation) =>
    this.props.selectedConversations.some((c) => c._id === conversation._id);

  // Phone Select mode stays on while anything is selected, so a selection made
  // on a wider screen is still there after rotating to a phone.
  isInSelectMode = () =>
    this.state.selectMode || this.props.selectedConversations.length > 0;

  enterSelectMode = (conversation) => {
    this.setState({ selectMode: true, actionSheetConversationId: null, showViewsSheet: false });
    if (conversation && !this.isConversationSelected(conversation)) {
      this.props.setSelectedConversations([...this.props.selectedConversations, conversation]);
    }
  };

  exitSelectMode = () => {
    this.setState({ selectMode: false });
    if (this.props.selectedConversations.length) {
      this.props.setSelectedConversations([]);
    }
  };

  toggleSelectAllVisible = (visibleConversations) => {
    const allSelected =
      visibleConversations.length > 0 &&
      visibleConversations.every((c) => this.isConversationSelected(c));
    this.props.setSelectedConversations(allSelected ? [] : visibleConversations);
  };

  openRowActions = (conversation) => {
    this.lastActionSheetConversation = conversation;
    this.setState({ actionSheetConversationId: conversation._id });
  };

  // The live row (so checks update after each change), or the last one seen
  // when an action moved it out of the current filter.
  getActionSheetConversation = () => {
    const { actionSheetConversationId } = this.state;
    if (!actionSheetConversationId) return this.lastActionSheetConversation || null;
    const live =
      this.props.customerConversations.find((c) => c._id === actionSheetConversationId) ||
      this.props.allCustomerConversations?.find((c) => c._id === actionSheetConversationId);
    if (live) this.lastActionSheetConversation = live;
    return this.lastActionSheetConversation || null;
  };

  toggleRowRead = (conversation) => {
    this.props.toggleMarkAsReadConversation({
      conversation,
      isRead: !isConversationReadForUser(conversation, this.props.auth.user),
    });
  };

  toggleRowOpen = (conversation) => {
    this.props.toggleConversationOpen({ conversation, open: !conversation.open });
  };

  toggleRowPin = async (conversation) => {
    const pinnedForMe = (conversation._pinnedByUsers || []).some(
      (_user) => String(_user) === String(this.props.auth.user._id),
    );
    const res = await this.props.toggleConversationPin({
      conversation,
      scope: 'user',
      pinned: !pinnedForMe,
    });
    if (res?.error) {
      this.props.displayNotification({
        message: res.message || 'Error updating conversation pin',
        error: true,
      });
    }
  };

  down(e) {
    if (this.props.active !== 'webchat') return;
    if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      this.props.handleCustomerDialogIsOpen(!this.props.customerLookupDialogIsOpen);
    }
  }

  toggleConversationSelect = (conversation) => {
    if (this.props.selectedConversations.find((c) => c._id === conversation._id)) {
      this.props.setSelectedConversations(
        this.props.selectedConversations.filter((c) => c._id !== conversation._id),
      );
    } else {
      this.props.setSelectedConversations([...this.props.selectedConversations, conversation]);
    }
  };

  // The conversation's AI draft, with a reply or only a note for the team.
  getConversationAiDraftEntry = (conversation) => {
    if (!conversation?._id) return null;

    const hasReduxDraftState = conversation._id in (this.props.aiDraftByConversation || {});
    return hasReduxDraftState
      ? this.props.aiDraftByConversation?.[conversation._id]
      : conversation.aiDraft;
  };

  getConversationAiDraft = (conversation) => {
    const draft = this.getConversationAiDraftEntry(conversation);
    return draft?.draftText?.trim() ? draft : null;
  };

  renderTypingIndicator = (_customerConversation) => {
    const { typingIndicator } = this.props;
    const typers = typingIndicator.filter(
      (t) => t._customerConversation === _customerConversation
    );

    if (!typers.length) return null;

    return (
      <>
        {formatTypingNames(typers.map((t) => t.whoIsTyping))}
        <span className="one-dot">.</span>
        <span className="two-dot">.</span>
        <span className="three-dot">.</span>
      </>
    );
  };

  // Resolve the participant name for a group message. message._customer may be
  // a populated customer object or a bare id string.
  getGroupParticipantName = (message, conversation) => {
    if (!message?._customer || !conversation?.isGroup) return '';
    const raw = message._customer;
    const senderId = raw?._id ? raw._id.toString() : raw?.toString?.() || raw;
    const participant = (conversation._customers || []).find((c) => {
      const id = c?._id ? c._id.toString() : c;
      return id === senderId;
    });
    return participant?.name || (typeof raw === 'object' ? raw?.name : '') || '';
  };

  getLastMessageText = (lastMessage, _customerConversation) => {
    const { text, media, sender } = lastMessage;

    const typingIndicator = this.renderTypingIndicator(_customerConversation);

    if (typingIndicator) {
      return typingIndicator;
    }

    // Only prefix the preview when it adds meaning: the AI label (so a bot
    // reply is obvious) and group participant names (so you know who spoke).
    // Human/outbound replies are left unprefixed to maximize content space.
    let senderName = '';
    if (sender === 'ai') {
      senderName = this.getMessageSentBy(lastMessage);
    } else if (_customerConversation?.isGroup && sender === 'customer') {
      senderName = this.getGroupParticipantName(lastMessage, _customerConversation);
    }

    if (!text) {
      if (media) {
        return senderName ? `${senderName} sent attachment` : 'Sent Attachment';
      }
    }

    const finalText = senderName ? `${senderName}: ${text || ''}` : text || '';

    return finalText?.substring(0, 300);
  };

  getMessageTime = (date) => {
    const momentObj = moment(date);
    const now = moment();

    // Same day: show time only (e.g., "11:00 AM")
    if (momentObj.isSame(now, 'day')) {
      return momentObj.format('LT');
    }

    // Within the past week: show day name (e.g., "Sunday")
    const oneWeekAgo = moment().subtract(6, 'days').startOf('day');
    if (momentObj.isAfter(oneWeekAgo)) {
      return momentObj.format('dddd');
    }

    // Older than a week: show full date
    // Only include year if it's not the current year
    if (momentObj.isSame(now, 'year')) {
      return momentObj.format('LTS');
    }
    return momentObj.format('ll');
  };

  getAssigneeName = (_assignee) => {
    const user = this.props.users.filter((u) => _assignee === u._id && u.active);
    return (user.length && user[0].name) || '';
  };

  getAssigneesDisplay = (_assignees, _assignee) => {
    const assignees = _assignees?.length > 0 ? _assignees : _assignee ? [_assignee] : [];
    const names = assignees.map((assignee) => this.getAssigneeName(assignee)).filter(Boolean);

    if (!names.length) {
      return { label: '', full: '' };
    }

    if (names.length === 1) {
      return { label: names[0], full: names[0] };
    }

    const maxChars = 48;
    const separator = ', ';
    let shown = [];

    for (let i = 0; i < names.length; i++) {
      const candidate = [...shown, names[i]];
      const remaining = names.length - candidate.length;
      const suffix = remaining > 0 ? `${separator}+${remaining}` : '';
      const candidateText = `${candidate.join(separator)}${suffix}`;

      if (candidateText.length <= maxChars || shown.length === 0) {
        shown = candidate;
      } else {
        break;
      }
    }

    const remaining = names.length - shown.length;
    const label =
      remaining > 0 ? `${shown.join(separator)}${separator}+${remaining}` : shown.join(separator);

    return { label, full: names.join(separator) };
  };

  getDepartmentName = (_department) => {
    const department = this.props.departments.find((d) => d._id === _department);
    return department?.name || '';
  };

  getConversationDate = (conversation) => {
    const date = new Date(conversation.latestMessageDate || conversation.updatedAt || 0).getTime();

    return Number.isFinite(date) ? date : 0;
  };

  isConversationPinnedForUser = (conversation) => {
    return !!this.getConversationPinScope(conversation);
  };

  getConversationPinScope = (conversation) => {
    if (conversation.pinnedForBusiness) {
      return 'business';
    }

    const _user = this.props.auth.user._id;

    const isPinnedForUser = (conversation._pinnedByUsers || []).some(
      (_pinnedUser) => _pinnedUser.toString() === _user,
    );

    return isPinnedForUser ? 'user' : null;
  };

  getConversationPinTitle = (conversation) => {
    if (conversation.pinnedForBusiness) {
      return 'Pinned for everyone';
    }

    return 'Pinned for you';
  };

  renderConversationContextMenu = (conversation) => {
    return (
      <CustomerConversationContextMenuContent
        conversation={conversation}
        onCreateFolder={() => this.setState({ showCreateFolderDialog: true })}
        onShowVoiceUpsell={() => this.setState({ showVoiceUpsell: true })}
        onSelectConversation={() => {
          if (!this.isConversationSelected(conversation)) this.toggleConversationSelect(conversation);
        }}
      />
    );
  };

  sortConversationByPinnedThenLatest = (a, b) => {
    const aPinned = this.isConversationPinnedForUser(a);
    const bPinned = this.isConversationPinnedForUser(b);

    if (aPinned !== bPinned) {
      return aPinned ? -1 : 1;
    }

    return this.getConversationDate(b) - this.getConversationDate(a);
  };

  // calculates the number of tags to render based on the length of the tags
  // tags are rendered without an accordion if they are less than or equal to MAX_TAG_CHAR_LENGTH
  calculateTagsToRender = (tags) => {
    if (tags.length < 3) {
      return tags.length;
    }
    let currentLength = 0;
    let tagCount = 0;

    for (let i = 0; i < tags.length; i++) {
      const tagLength = tags[i].length;
      const calculatedTagLength = currentLength + tagLength;

      if (calculatedTagLength <= MAX_TAG_CHAR_LENGTH) {
        currentLength += tagLength;
        tagCount++;
      } else {
        break; // Stop if adding the next tag exceeds the maxCharLength
      }
    }

    return tagCount;
  };

  getMessageSentBy = (message) => {
    if (message.sender === 'ai') {
      return 'AI Employee';
    }

    if (!this.props.users) {
      return 'You';
    }

    if (message._messageAutomation && this.props.messageAutomations) {
      const messageAutomationArr = this.props.messageAutomations.filter(
        (ma) => ma._id === message._messageAutomation,
      );

      if (!messageAutomationArr.length) {
        return 'Message Automation';
      }

      return messageAutomationArr[0].title || 'Message Automation';
    }

    if (!message._user) {
      return 'Bot';
    }

    const nameArr = this.props.users.filter((user) => user._id === message._user);

    if (!nameArr.length) {
      return 'You';
    }

    return nameArr[0].name || nameArr[0].username || 'You';
  };

  // group conversations carry _customers instead of _customer
  conversationHasParticipants = (conversation) =>
    Boolean(conversation?._customer || (conversation?.isGroup && conversation?._customers?.length));

  renderLastMessage = (conversation) => {
    if (this.conversationHasParticipants(conversation) && conversation.messages) {
      const typingIndicator = this.renderTypingIndicator(conversation._id);

      if (typingIndicator) {
        return <p className="text-sm leading-5 text-muted-foreground line-clamp-2 break-words max-md:text-[15px]">{typingIndicator}</p>;
      }

      const aiDraft = this.getConversationAiDraft(conversation);

      if (aiDraft) {
        return (
          <p className="text-sm leading-5 text-muted-foreground line-clamp-2 break-words italic max-md:text-[15px]">
            <span className="font-medium text-foreground/80 not-italic">AI draft:</span>{' '}
            {aiDraft.draftText?.substring(0, 300)}
          </p>
        );
      }

      const nonSystemClosedMessages = conversation.messages.filter(
        (m) =>
          // internal notes are team-only and shouldn't masquerade as the
          // latest conversation message in the list preview
          m.sender !== 'note' &&
          !(
            m.text &&
            (m.text.includes('this conversation') ||
              m.text.includes('assigned conversation') ||
              m.text.includes('Conversation opened') ||
              m.text.includes('Conversation closed')) &&
            m.sender === 'event'
          ),
      );

      // are there messages?
      const messageCount = nonSystemClosedMessages.length;

      // if there are no messages, set "empty conversation" defaults
      const latestMessage = messageCount
        ? nonSystemClosedMessages[nonSystemClosedMessages.length - 1]
        : {
            sender: 'event',
            text: 'No messages in this conversation',
            media: null,
          };

      return (
        <p className="text-sm leading-5 text-muted-foreground line-clamp-2 break-words max-md:text-[15px]">
          {this.getLastMessageText(latestMessage, conversation._id)}
        </p>
      );
    }
    return null;
  };

  renderName = (conversation, businessGroup) => {
    if (this.conversationHasParticipants(conversation) && conversation.messages) {
      // notes don't count as conversation activity, so they don't move the time
      // (the list order and preview ignore them too)
      const latestMessage = conversation.messages.findLast((m) => m.sender !== 'note') || {
        createdAt: conversation.createdAt,
      };

      const { isRead } = conversation;

      // if the user hasOwnIsReadIndicators, show the unread indicator if their ID is not in the _readByUsers array
      // otherwise, show the unread indicator if the conversation is not read
      let showUnreadIndicator = false;

      if (this.props.auth.user.hasOwnIsReadIndicators && conversation._readByUsers) {
        showUnreadIndicator = !conversation._readByUsers.includes(this.props.auth.user._id);
      } else {
        showUnreadIndicator = !isRead;
      }

      const { createdAt } = latestMessage;
      const assigneeDisplay = this.getAssigneesDisplay(
        conversation._assignees,
        conversation._assignee,
      );
      const pinScope = this.getConversationPinScope(conversation);
      const isPinned = !!pinScope;
      const pinButtonTitle = isPinned
        ? this.getConversationPinTitle(conversation)
        : 'Pin conversation';
      const aiDraft = this.getConversationAiDraft(conversation);
      // A note-only draft: the AI couldn't answer and left a note for the team.
      const aiNeedsYou =
        !aiDraft && aiDraftChip(this.getConversationAiDraftEntry(conversation)) === 'needs_you';
      const { isMobile } = this.props;

      return (
        <div className="flex w-full flex-col gap-0.5">
          <div className="flex items-start gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <p
                className={cn(
                  'truncate text-[15px] font-normal leading-5',
                  'max-md:text-[16px] max-md:leading-[1.3rem]',
                  isMobile && showUnreadIndicator && 'max-md:font-semibold',
                )}
              >
                {getConversationListName(conversation, this.props.isActivated) || '-'}
              </p>
              {isMobile ? (
                isPinned && (
                  <Pin
                    className={cn(
                      'size-3.5 shrink-0',
                      pinScope === 'business'
                        ? 'text-red-500 dark:text-red-400'
                        : 'text-muted-foreground',
                    )}
                    aria-label={pinButtonTitle}
                    role="img"
                  />
                )
              ) : (
              <span className="group/pin flex size-3 shrink-0 items-center justify-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'size-3 shrink-0 rounded [&_svg]:size-3',
                        pinScope === 'business'
                          ? 'text-red-500 hover:text-red-600 focus-visible:text-red-600 dark:text-red-400 dark:hover:text-red-300'
                          : 'text-muted-foreground hover:text-foreground focus-visible:text-foreground',
                        !isPinned &&
                          'opacity-0 group-hover/pin:opacity-100 group-focus-within/pin:opacity-100 data-[state=open]:opacity-100',
                      )}
                      aria-label={pinButtonTitle}
                      title={pinButtonTitle}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                      }}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <Pin className="size-2.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-48"
                    align="start"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <ConversationPinMenuItems
                      auth={this.props.auth}
                      conversation={conversation}
                      displayNotification={this.props.displayNotification}
                      ItemComponent={DropdownMenuItem}
                      toggleConversationPin={this.props.toggleConversationPin}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
              )}
              {hasAiEmployeeProduct(this.props.auth) && aiDraft && (
                <span
                  className="shrink-0 inline-flex items-center gap-0.5 rounded bg-emerald-100 px-1 text-[9px] font-semibold text-emerald-700 max-md:text-[11px] dark:bg-emerald-950 dark:text-emerald-300"
                  title="AI draft ready"
                >
                  <Sparkles className="size-2.5" />
                  Draft
                </span>
              )}
              {hasAiEmployeeProduct(this.props.auth) && aiNeedsYou && (
                <span
                  className="shrink-0 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-700 max-md:text-[11px] dark:bg-amber-950 dark:text-amber-300"
                  title="The AI couldn't answer this and left a note for your team"
                >
                  <TriangleAlert className="size-2.5" />
                  Needs you
                </span>
              )}
              {hasAiEmployeeProduct(this.props.auth) &&
                (conversation.handoffStatus === 'pending' ||
                  this.props.escalationsByConversation?.[conversation._id]) && (
                  <span
                    className="shrink-0 text-[9px] font-semibold text-amber-600 max-md:text-[11px] dark:text-amber-400 bg-amber-100 dark:bg-amber-900 px-1 rounded"
                    title="Escalated — needs attention"
                  >
                    Escalated
                  </span>
                )}
              {hasAiEmployeeProduct(this.props.auth) &&
                this.props.aiStatusByConversation?.[conversation._id] === 'thinking' && (
                  <Orb size={14} active label="AI is responding" />
                )}
              {hasAiEmployeeProduct(this.props.auth) &&
                !aiDraft &&
                !aiNeedsYou &&
                latestMessage.sender === 'ai' &&
                this.props.aiStatusByConversation?.[conversation._id] !== 'thinking' &&
                !conversation.handoffStatus?.match(/pending/) &&
                !conversation.aiPaused && (
                  <span className="shrink-0 text-[9px] font-semibold text-blue-600 max-md:text-[11px] dark:text-blue-400 bg-blue-100 dark:bg-blue-900 px-1 rounded">
                    AI
                  </span>
                )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="whitespace-nowrap text-xs font-normal text-muted-foreground max-md:text-[13px]">
                {this.getMessageTime(createdAt)}
              </span>
              {showUnreadIndicator && (
                <span className="inline-flex shrink-0 items-center">
                  <span className="size-2 rounded-full bg-primary max-md:size-2.5" aria-hidden="true" />
                  <span className="sr-only">Unread conversation</span>
                </span>
              )}
            </div>
          </div>
          {(conversation._department || assigneeDisplay.label || this.props.auth.user.isEnterpriseEnabled) && (
            <div className="flex min-w-0 items-center gap-x-1.5 text-xs leading-4 text-muted-foreground max-md:text-[13px] max-md:leading-[1.1rem]">
              {(conversation._department ||
                conversation._assignees?.length > 0 ||
                conversation._assignee) && (
                <div className="flex min-w-0 items-center gap-1">
                  {conversation._department ? (
                    <>
                      <Users size={12} className="shrink-0" />
                      <span className="truncate">
                        {this.getDepartmentName(conversation._department)}
                      </span>
                    </>
                  ) : (
                    <>
                      <User size={12} className="shrink-0" />
                      <span className="truncate" title={assigneeDisplay.full}>
                        {assigneeDisplay.label}
                      </span>
                    </>
                  )}
                </div>
              )}
              {this.props.auth.user.isEnterpriseEnabled && (
                <div className="ml-auto flex min-w-0 items-center gap-x-0.5 text-muted-foreground">
                  <MapPin size={12} className="shrink-0" />
                  <span className="truncate">
                    {(() => {
                      const business = businessGroup.find((b) => b._id === conversation._business);
                      return business ? business.name : '';
                    })()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  renderTagsInCustomerList = (customer) => {
    if (!customer?.tags) {
      return null;
    }

    const validTags = customer.tags.filter(Boolean);

    if (validTags.length === 0) {
      return;
    }

    const numberOfTagsToRender = this.calculateTagsToRender(validTags);
    const tagsTrigger = validTags.slice(0, numberOfTagsToRender);

    const tagClassName = 'text-[11px] font-normal rounded-full px-2 py-0 h-5';

    // Phones show a couple of tags and a count, with no expander inside the
    // tappable row (a tap there would also open the conversation).
    if (this.props.isMobile) {
      const shown = validTags.slice(0, 2);
      const hidden = validTags.length - shown.length;
      return (
        <div className="flex min-w-0 items-center gap-1 overflow-hidden pt-0.5">
          {shown.map((tag) => (
            <Badge variant="secondary" className={cn(tagClassName, 'max-w-[45%] truncate text-[12px]')} key={tag}>
              {tag}
            </Badge>
          ))}
          {hidden > 0 && (
            <Badge variant="secondary" className={cn(tagClassName, 'shrink-0 text-[12px]')}>
              +{hidden}
            </Badge>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 flex-wrap w-full">
        {validTags.length > numberOfTagsToRender ? (
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="tags" className="w-full border-none">
              <AccordionTrigger className="justify-between w-full py-0 hover:no-underline">
                <div className="flex items-center gap-1 flex-wrap">
                  {tagsTrigger?.map((tag) => (
                    <Badge
                      variant={
                        this.props.activeConversation._customer?._id === customer._id
                          ? 'outline'
                          : 'secondary'
                      }
                      className={tagClassName}
                      key={tag}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </AccordionTrigger>
              <AccordionContent className="py-1 flex items-center w-full justify-start flex-wrap gap-1">
                {customer.tags.slice(numberOfTagsToRender, customer.tags.length).map((tag) => (
                  <Badge
                    key={tag}
                    variant={
                      this.props.activeConversation._customer?._id === customer._id
                        ? 'outline'
                        : 'secondary'
                    }
                    className={tagClassName}
                  >
                    {tag}
                  </Badge>
                ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : (
          validTags.map((tag) => (
            <Badge
              variant={
                this.props.activeConversation._customer?._id === customer._id
                  ? 'outline'
                  : 'secondary'
              }
              className={tagClassName}
              key={tag}
            >
              {tag}
            </Badge>
          ))
        )}
      </div>
    );
  };

  // The phone list in the order the rows show, for Select all.
  getVisibleConversations = () =>
    [...this.filterConversationsByChannel(this.props.customerConversations || [])].sort(
      this.sortConversationByPinnedThenLatest,
    );

  // A phone row: the same name, preview and tag lines as the desktop row, with
  // a larger avatar, swipe actions and a long-press action sheet.
  renderMobileConversationRow = ({
    conversation,
    index,
    disableSelect,
    inSelectMode,
    onGoingCallForConversation,
    userOnCall,
  }) => {
    const { auth, businessGroup, isActivated } = this.props;
    const userId = String(auth.user?._id);
    const pinnedForMe = (conversation._pinnedByUsers || []).some((_user) => String(_user) === userId);

    return (
      <MobileConversationRow
        key={conversation._id || conversation._customer?._id}
        selectMode={inSelectMode}
        selected={this.isConversationSelected(conversation)}
        disabled={disableSelect}
        isRead={isConversationReadForUser(conversation, auth.user)}
        isOpen={conversation.open}
        isPinned={pinnedForMe}
        onOpen={() => {
          this.savedListScrollTop = this.listScrollRef?.scrollTop ?? null;
          this.props.onSelectConversation(conversation, index);
        }}
        onToggleSelect={() => this.toggleConversationSelect(conversation)}
        onShowActions={() => this.openRowActions(conversation)}
        onToggleRead={() => this.toggleRowRead(conversation)}
        onToggleOpen={() => this.toggleRowOpen(conversation)}
        onTogglePin={() => this.toggleRowPin(conversation)}
      >
        {/* The initials would be read before the name; the name says it all. */}
        <div className="my-2.5 mr-3 size-12 shrink-0 self-start" aria-hidden="true">
          {conversation.isGroup ? (
            <GroupAvatar
              customers={getConversationCustomers(conversation)}
              business={conversation._business}
              className="size-12 [&>*]:size-7 [&>*]:text-[11px] [&>*>span]:text-[11px]"
            />
          ) : (
            <CustomerAvatar
              customer={
                conversation._customer && {
                  ...conversation._customer,
                  _business: conversation._customer._business || conversation._business,
                }
              }
              fallbackName={maskPhone(
                conversation._customer?.name || conversation.title || '',
                isActivated,
              )}
              fallbackClassName="text-[17px] font-medium"
              colorSeed={
                getConversationDisplayName(conversation) ||
                conversation._customer?._id ||
                conversation._id
              }
              className="size-12"
            />
          )}
        </div>
        <div
          className="flex min-w-0 flex-1 flex-col gap-0.5 border-b border-border/60 py-2.5 pr-4"
          data-testid="customer-list-item"
        >
          {this.renderName(conversation, businessGroup)}
          {this.renderLastMessage(conversation)}
          {this.renderTagsInCustomerList(conversation._customer)}
          {onGoingCallForConversation && (
            <div className="flex items-center pt-0.5">
              <div className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 dark:border-emerald-800 dark:bg-emerald-950/50">
                <Phone className="size-3 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                <SoundWave color="emerald" />
                <span className="text-[12px] font-medium text-emerald-700 dark:text-emerald-300">
                  {userOnCall?.name || userOnCall?.username || 'On a call'}
                </span>
              </div>
            </div>
          )}
        </div>
      </MobileConversationRow>
    );
  };

  renderCustomerConversationList = () => {
    const { customerConversations, activeConversation, loading, businessGroup, voice, users } =
      this.props;

    const { ongoingCalls } = voice;

    const { openConversationsSelected, isMobile } = this.props;

    if (loading && isMobile) {
      return (
        <div className="grow overflow-hidden">
          <MobileRowSkeletons count={Math.min(Math.max(customerConversations.length, 6), 10)} />
        </div>
      );
    }

    if (loading) {
      const skeletons = customerConversations.length
        ? customerConversations.map((c) => <ConversationSkeleton key={c._id} />)
        : [
            <ConversationSkeleton key="1" />,
            <ConversationSkeleton key="2" />,
            <ConversationSkeleton key="3" />,
            <ConversationSkeleton key="5" />,
            <ConversationSkeleton key="4" />,
            <ConversationSkeleton key="6" />,
            <ConversationSkeleton key="7" />,
          ];
      return (
        <div
          className="flex-1 min-h-0 overflow-y-auto p-5"
          style={{
            scrollbarWidth: 'none',
            MsOverflowStyle: 'none',
            WebkitScrollbar: 'none',
          }}
        >
          {skeletons}
        </div>
      );
    }

    // Apply channel filter to conversations
    const filteredConversations = this.filterConversationsByChannel(customerConversations);

    const sortedAndFilteredConversations = [...filteredConversations].sort(
      this.sortConversationByPinnedThenLatest,
    );

    if (!filteredConversations?.length) {
      const filterDescription = this.getFilterDescription();

      return (
        <div className={cn('flex flex-col', isMobile ? 'grow' : 'flex-1 min-h-0')}>
          <div
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-4 p-4',
              !isMobile && 'overflow-y-auto',
            )}
          >
            {openConversationsSelected && (
              <div style={{ width: '50%' }}>
                <InboxEmpty color={this.props.brandColor} />
              </div>
            )}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">No {filterDescription}.</p>
            </div>
            <div className="pb-20 max-md:pb-6">{this.renderLoadMoreButton()}</div>
          </div>
        </div>
      );
    }

    const inSelectMode = isMobile && this.isInSelectMode();

    return (
      <TabsContent
        value={this.props.openConversationsSelected ? 'OPEN' : 'CLOSED'}
        className={cn('m-0 flex flex-col', isMobile ? 'grow' : 'flex-1 min-h-0')}
      >
        {/* On phones the whole screen scrolls (see render), not this list. */}
        <div
          ref={isMobile ? undefined : this.setListScrollRef}
          className={cn(!isMobile && 'flex-1 overflow-y-auto')}
          style={{
            scrollbarWidth: 'none',
          }}
        >
          <div
            className={cn(
              'flex flex-col pb-20',
              isMobile && 'pb-2',
            )}
          >
            {sortedAndFilteredConversations.map((conversation, i) => {
              const onGoingCallForConversation = findActiveCustomerCall(
                ongoingCalls,
                conversation._customer,
              );

              const userOnCall = onGoingCallForConversation
                ? users.find((user) => user._id === onGoingCallForConversation._user)
                : null;

              const disableSelect = this.props.disableConversationSelect;

              if (isMobile) {
                return this.renderMobileConversationRow({
                  conversation,
                  index: i,
                  disableSelect,
                  inSelectMode,
                  onGoingCallForConversation,
                  userOnCall,
                });
              }

              return (
                <ContextMenu key={conversation._id || conversation._customer?._id}>
                  <ContextMenuTrigger asChild>
                    {/* biome-ignore lint/a11y/useSemanticElements: This row contains a nested checkbox, so it cannot be a native button. */}
                    <div
                      role="button"
                      tabIndex={disableSelect ? -1 : 0}
                      aria-disabled={disableSelect || undefined}
                      aria-current={activeConversation._id === conversation._id ? 'true' : undefined}
                      onClick={() => {
                        if (disableSelect) {
                          return;
                        }
                        this.props.onSelectConversation(conversation, i);
                      }}
                      onKeyDown={(e) => {
                        if (disableSelect || e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          this.props.onSelectConversation(conversation, i);
                        }
                      }}
                      className={cn(
                        'group/conversation flex flex-row items-start gap-3 px-4 py-3.5 text-left text-sm transition-colors cursor-pointer border-b border-border/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                        activeConversation._id === conversation._id
                          ? 'bg-stone-100 dark:bg-stone-800/70'
                          : 'hover:bg-stone-50 dark:hover:bg-stone-900/60',
                        disableSelect && 'opacity-95 cursor-default',
                      )}
                    >
                      <div className="relative size-10 shrink-0 group self-start">
                        {conversation.isGroup ? (
                          <GroupAvatar
                            customers={getConversationCustomers(conversation)}
                            business={conversation._business}
                            className={cn(
                              'size-10 [&>*]:size-6 [&>*]:text-[10px] [&>*>span]:text-[10px] transition-opacity duration-200',
                              this.props.selectedConversations.includes(conversation)
                                ? 'opacity-0'
                                : 'group-hover:opacity-50',
                            )}
                          />
                        ) : (
                          <CustomerAvatar
                            customer={conversation._customer && {
                              ...conversation._customer,
                              _business:
                                conversation._customer._business || conversation._business,
                            }}
                            fallbackName={maskPhone(
                              conversation._customer?.name || conversation.title || '',
                              this.props.isActivated,
                            )}
                            fallbackClassName="text-[15px] font-medium"
                            colorSeed={
                              getConversationDisplayName(conversation) ||
                              conversation._customer?._id || conversation._id
                            }
                            className={cn(
                              'size-10 transition-opacity duration-200',
                              this.props.selectedConversations.includes(conversation)
                                ? 'opacity-0'
                                : 'group-hover:opacity-50',
                            )}
                          />
                        )}
                        <Checkbox
                          aria-label={`Select ${getConversationListName(conversation, this.props.isActivated) || 'conversation'}`}
                          disabled={disableSelect}
                          className={cn(
                            'absolute inset-0 transition-opacity duration-200 flex items-center justify-center size-10 rounded-full border-2',
                            this.props.selectedConversations.includes(conversation)
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                            // Touch screens can't hover to reveal it: a tap on the avatar
                            // opens the conversation until something is selected.
                            this.props.selectedConversations.length
                              ? 'touch:opacity-100'
                              : 'touch:pointer-events-none',
                          )}
                          checked={this.props.selectedConversations.includes(conversation)}
                          onCheckedChange={() => {
                            this.toggleConversationSelect(conversation);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      </div>
                      <div
                        className="flex flex-1 flex-col gap-1 min-w-0"
                        data-testid="customer-list-item"
                      >
                        {this.renderName(conversation, businessGroup)}

                        {this.renderLastMessage(conversation)}
                        {this.renderTagsInCustomerList(conversation._customer)}
                        {onGoingCallForConversation && (
                          <div className="flex items-center">
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 rounded-full">
                              <Phone className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                              <SoundWave color="emerald" />
                              <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                                {userOnCall?.name || userOnCall?.username}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </ContextMenuTrigger>
                  {this.renderConversationContextMenu(conversation)}
                </ContextMenu>
              );
            })}
            {isMobile && <div ref={this.setLoadMoreSentinel} aria-hidden="true" className="h-px" />}
            {this.renderLoadMoreButton()}
          </div>
        </div>
      </TabsContent>
    );
  };

  renderLoadMoreButton = () => {
    const { customerConversations, loadingMoreConversations, loading } = this.props;

    if (loadingMoreConversations || loading) {
      return (
        <div className="flex justify-center items-center py-4">
          <Loader2 className="size-4 text-muted-foreground animate-spin" />
        </div>
      );
    }

    if (customerConversations) {
      const shortLabel = this.getFilterShortLabel();

      return (
        <button
          type="button"
          onClick={() => this.props.getNextCustomers(this.state.channelFilter)}
          className="w-full py-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors max-md:min-h-11 max-md:text-[13px] max-md:active:opacity-60"
        >
          Load more {shortLabel} conversations
        </button>
      );
    }

    return null;
  };

  toggleShowOpenOrClosedMessages = (newState) => {
    const openConversationsSelected = newState === 'OPEN';
    this.props.setOpenOrClosedConversationsSelected(openConversationsSelected);
  };

  toggleShowOnlyUnreadConversations = (newState) => {
    this.props.setShowOnlyUnreadConversations(newState);
  };

  handleChannelFilterChange = (channel) => {
    this.setState({ channelFilter: channel });
    this.props.setCustomerConversationChannelFilter(channel);
  };

  filterConversationsByChannel = (conversations) => {
    const { channelFilter } = this.state;

    if (!channelFilter || channelFilter === 'All') {
      return conversations;
    }

    if (channelFilter) {
      return conversations.filter((convo) => {
        // Default conversations without source to 'sms' (SMS)
        const conversationSource = convo.source || 'sms';
        return conversationSource === channelFilter;
      });
    }

    return conversations;
  };

  render() {
    const { toolbarMode } = this.state;
    const iconTabs = toolbarMode !== 'full';
    const isCompactToolbar = toolbarMode === 'iconsOnly';
    const callDisabled =
      this.props.voice.status === 'connecting' ||
      this.props.voice.status === 'ringing' ||
      this.props.voice.status === 'answered';
    const openVoiceDialer = () => this.setState({ voiceDialerDialogIsOpen: true });
    const openNewConversation = () => this.props.handleCustomerDialogIsOpen(true);

    const { isMobile } = this.props;
    const inSelectMode = isMobile && this.isInSelectMode();
    const visibleConversations = isMobile ? this.getVisibleConversations() : [];
    const allVisibleSelected =
      visibleConversations.length > 0 &&
      visibleConversations.every((c) => this.isConversationSelected(c));
    const actionSheetConversation = isMobile ? this.getActionSheetConversation() : null;

    return (
      // Phones: the whole screen is one scroll column, so the large title,
      // search and filters scroll away under the bar like an iOS list and the
      // rows get the height (landscape phones are only ~375px tall).
      <div
        ref={isMobile ? this.setListScrollRef : undefined}
        onScroll={isMobile ? this.handleMobileListScroll : undefined}
        className={cn(
          'flex flex-col h-full m-0',
          isMobile
            ? 'relative overflow-x-hidden overflow-y-auto overscroll-contain scroll-pt-14'
            : 'overflow-hidden',
        )}
      >
        {isMobile && (
          <InboxMobileHeader
            titleCollapsed={this.state.titleCollapsed}
            largeTitleRef={this.setLargeTitleRef}
            title={getInboxViewTitle(this.props.assignedTypeSelected, {
              users: this.props.users,
              departments: this.props.departments,
              folders: this.props.folders,
              businessGroup: this.props.businessGroup,
            })}
            selectMode={inSelectMode}
            selectedCount={this.props.selectedConversations.length}
            allSelected={allVisibleSelected}
            onEnterSelect={() => this.enterSelectMode()}
            onToggleSelectAll={() => this.toggleSelectAllVisible(visibleConversations)}
            onDone={this.exitSelectMode}
            onOpenViews={() => this.setState({ showViewsSheet: true })}
            onCompose={openNewConversation}
          />
        )}
        <Tabs
          defaultValue="OPEN"
          value={this.props.openConversationsSelected ? 'OPEN' : 'CLOSED'}
          className={cn('flex flex-col', isMobile ? 'grow shrink-0' : 'h-full overflow-hidden')}
        >
          {/* Unified header toolbar */}
          <div
            ref={this.setToolbarRef}
            className={cn(
              'flex flex-wrap shrink-0 items-center justify-between px-3 pt-3 pb-2 max-md:hidden',
              iconTabs ? 'gap-1' : 'gap-3',
            )}
          >
            <div className={cn('flex shrink-0 items-center', iconTabs ? 'gap-1' : 'gap-2')}>
              {this.hasVoiceProduct() &&
                (isCompactToolbar ? (
                  <IconButton
                    variant="outline"
                    toolTipTitle="Call"
                    aria-label="Call"
                    onClick={openVoiceDialer}
                    disabled={callDisabled}
                  >
                    <Phone className="size-4" />
                  </IconButton>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openVoiceDialer}
                    disabled={callDisabled}
                    className="h-9 gap-2 rounded-lg font-normal shadow-none"
                  >
                    <Phone className="size-4" />
                    <span>Call</span>
                  </Button>
                ))}

              {isCompactToolbar ? (
                <IconButton
                  variant="default"
                  toolTipTitle="New conversation"
                  data-onboarding="inbox-new-conversation"
                  aria-label="New conversation"
                  onClick={openNewConversation}
                >
                  <SquarePen className="size-4" />
                </IconButton>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={openNewConversation}
                  className="h-9 gap-2 rounded-lg font-normal shadow-none"
                  data-onboarding="inbox-new-conversation"
                >
                  <SquarePen className="size-4" />
                  <span>New</span>
                </Button>
              )}
            </div>

            <div className={cn('flex shrink-0 items-center', iconTabs ? 'gap-1' : 'gap-2')}>
              <TabsList className="h-9 rounded-lg border border-border/60 bg-stone-100 dark:bg-stone-900">
                {[
                  { value: 'OPEN', label: 'Open', Icon: MailOpen },
                  { value: 'CLOSED', label: 'Closed', Icon: CheckCircle },
                ].map(({ value, label, Icon }) => {
                  const trigger = (
                    <TabsTrigger
                      key={value}
                      className={cn(
                        'h-7 rounded-md font-normal data-[state=active]:bg-primary data-[state=active]:text-primary-foreground',
                        // The tooltip trigger overwrites data-state, so icon mode relies on aria-selected
                        iconTabs && 'aria-selected:bg-primary aria-selected:text-primary-foreground',
                        iconTabs && 'px-2',
                      )}
                      value={value}
                      aria-label={iconTabs ? label : undefined}
                      onClick={() => this.toggleShowOpenOrClosedMessages(value)}
                    >
                      {iconTabs ? <Icon className="size-4" /> : label}
                    </TabsTrigger>
                  );

                  if (!iconTabs) return trigger;

                  return (
                    <Tooltip key={value} delayDuration={0}>
                      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                      <TooltipContent>{label}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </TabsList>

              {!iconTabs && <div className="h-5 w-px bg-border" />}

              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Toggle
                    aria-label="Show unread only"
                    onPressedChange={this.toggleShowOnlyUnreadConversations}
                    pressed={this.props.showOnlyUnreadConversations}
                    // aria-pressed, not data-state: the tooltip trigger overwrites data-state
                    className="h-9 gap-1.5 px-2.5 aria-pressed:bg-primary/10"
                  >
                    <MessageSquareDot
                      className={`size-4 ${
                        this.props.showOnlyUnreadConversations
                          ? 'text-primary'
                          : 'text-muted-foreground'
                      }`}
                    />
                  </Toggle>
                </TooltipTrigger>
                <TooltipContent>
                  {this.props.showOnlyUnreadConversations
                    ? 'Showing unread only'
                    : 'Show unread only'}
                </TooltipContent>
              </Tooltip>

              <ChannelFilter
                channelFilter={this.state.channelFilter || 'All'}
                onChannelChange={this.handleChannelFilterChange}
              />
            </div>
          </div>

          {/* Search / Action bar row */}
          <div className="shrink-0 px-3 pb-2.5 border-b border-border/60 max-md:border-b-0 max-md:px-4 max-md:pb-2">
            <AnimatePresence mode="wait">
              {this.props.selectedConversations.length === 0 || isMobile ? (
                <CustomerSearchBar
                  key="search-bar"
                  isMobile={isMobile}
                  initialCustomerSearch={this.customerSearchText}
                  onCustomerSearchChange={this.setCustomerSearchText}
                  searchForMessage={this.props.searchForMessage}
                  clearMessageSearch={this.props.clearMessageSearch}
                  addConversationToCustomerConversations={
                    this.props.addConversationToCustomerConversations
                  }
                  setOpenOrClosedConversationsSelected={
                    this.props.setOpenOrClosedConversationsSelected
                  }
                  auth={this.props.auth}
                  brandColor={this.props.brandColor}
                  businessGroup={this.props.businessGroup}
                  isActivated={this.props.isActivated}
                  setInitialCreateNewCustomer={(searchBarValueCustomer) => {
                    this.setState(
                      {
                        customerLookupInitialValue: searchBarValueCustomer,
                      },
                      this.props.handleCustomerDialogIsOpen(true),
                    );
                  }}
                />
              ) : (
                <ActionBar
                  key="action-bar"
                  selectedCount={this.props.selectedConversations.length}
                  onClear={() => {
                    this.props.setSelectedConversations([]);
                  }}
                  onMarkOpen={() => {
                    this.props.setOpenOrClosedConversationsSelected(true);
                  }}
                  onMarkClosed={() => {
                    this.props.setOpenOrClosedConversationsSelected(false);
                  }}
                  onChangeAssignee={(assignee) => {
                    this.props.setSelectedAssignee(assignee);
                  }}
                  onChangeFolder={(folder) => {
                    this.props.setSelectedFolder(folder);
                  }}
                  onAddTag={(tag) => {
                    this.props.setSelectedTag(tag);
                  }}
                  assignees={this.props.assignees}
                  folders={this.props.folders}
                  tags={this.props.tags}
                />
              )}
            </AnimatePresence>
          </div>

          {isMobile && (
            <InboxMobileFilters
              openConversationsSelected={this.props.openConversationsSelected}
              onOpenChange={(open) => this.toggleShowOpenOrClosedMessages(open ? 'OPEN' : 'CLOSED')}
              showOnlyUnread={!!this.props.showOnlyUnreadConversations}
              onUnreadChange={this.toggleShowOnlyUnreadConversations}
              channelFilter={this.state.channelFilter || 'All'}
              onChannelChange={this.handleChannelFilterChange}
            />
          )}

          {this.renderCustomerConversationList()}
        </Tabs>
        {inSelectMode && <MobileBulkBar onFinished={this.exitSelectMode} />}
        <HideMobileTabBar active={inSelectMode} />
        {isMobile && (
          <>
            <ConversationActionSheet
              conversation={actionSheetConversation}
              title={
                actionSheetConversation
                  ? getConversationListName(actionSheetConversation, this.props.isActivated)
                  : undefined
              }
              open={Boolean(this.state.actionSheetConversationId)}
              onOpenChange={(open) => {
                if (!open) this.setState({ actionSheetConversationId: null });
              }}
              onCreateFolder={() => this.setState({ showCreateFolderDialog: true })}
              onShowVoiceUpsell={() => this.setState({ showVoiceUpsell: true })}
              onSelectConversation={() => this.enterSelectMode(actionSheetConversation)}
            />
            <InboxViewsSheet
              open={this.state.showViewsSheet}
              onOpenChange={(open) => this.setState({ showViewsSheet: open })}
              onCreateFolder={() => this.setState({ showCreateFolderDialog: true })}
              onCall={this.hasVoiceProduct() ? openVoiceDialer : undefined}
              callDisabled={callDisabled}
              onSelect={() => this.enterSelectMode()}
            />
          </>
        )}
        <VoiceDialerDialog
          open={this.state.voiceDialerDialogIsOpen}
          onOpenChange={(open) => {
            this.setState({ voiceDialerDialogIsOpen: open });
          }}
        />
        <CreateFolderDialog
          open={this.state.showCreateFolderDialog}
          setOpen={(open) => this.setState({ showCreateFolderDialog: open })}
        />
        <AlertDialog
          open={this.state.showVoiceUpsell}
          onOpenChange={(open) => this.setState({ showVoiceUpsell: open })}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Voice Calling</AlertDialogTitle>
              <AlertDialogDescription>
                Call your customers directly from Chekkit with our Voice Calling feature. Contact us
                to add voice calling to your plan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction>Got it</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    brandColor: state.brandColor,
    isActivated: canExploreSignupDashboard(state.auth) ? true : state.auth.activated,
    auth: state.auth,
    departments: state.departments,
    tags: state.tags,
    users: state.users,
    messageAutomations: state.messageAutomations,
    typingIndicator: state.typingIndicator,
    businessGroup: state.businessGroup,
    folders: state.folders,
    selectedConversations: state.userInterface.webchat.selectedConversations,
    reseller: state.reseller,
    voice: state.voice,
    aiStatusByConversation: state.aiEmployee.statusByConversation,
    aiDraftByConversation: state.aiEmployee.draftByConversation,
    escalationsByConversation: state.aiEmployee.escalationsByConversation,
  };
}

export default connect(mapStateToProps, {
  displayNotification,
  setCustomerConversationChannelFilter,
  setOpenOrClosedConversationsSelected,
  setSelectedConversations,
  toggleConversationOpen,
  toggleConversationPin,
  toggleMarkAsReadConversation,
})(CustomerList);
